import { useState, Fragment, useCallback, useMemo } from 'react';
import { flatten, head, map } from 'lodash';

import { amplitudeService, ScreenName } from '../../../../../shared/amplitude';
import { SelectPhotoReviewList, ViewReviewDetail } from '../../../../../shared/amplitude/events/review';
import Alert from '../../../../../shared/components/Alert/Alert';

import { useReviewList } from '../../hooks';
import ReviewModal from '../ReviewModal/ReviewModal';
import ReviewModalImageList from '../ReviewModal/ReviewModalImageList';
import ReviewModalDetailContent from '../ReviewModal/ReviewModalDetailContent';
import { ShowMore, ThumbnailImage, Wrapper } from './styled-components';
import Loading from './Loading';
import { useAppSelector } from '../../../../../shared/store';
import type { ProductReview } from '../../ProductReview.service';
import { NoMainImageLogo } from '../../../../../shared/images';

interface Props {
  contentsProductNo: number;
}

const CONTENT_TYPE = {
  LIST: 'LIST',
  DETAIL: 'DETAIL',
};

const MAX_NUMBER_OF_REVIEW_GALLERY_IMAGE = 8;

export default function ReviewGallery({ contentsProductNo }: Props) {
  const productData = useAppSelector(({ productDetail }) => productDetail);

  const [currentContent, setCurrentContent] = useState(CONTENT_TYPE.LIST);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const { data, status, isLoading, hasNextPage, fetchNextPage } = useReviewList(contentsProductNo, {
    reviewType: 'IMAGE',
  });

  const flatPages = useMemo(() => flatten(map(data?.pages, ({ reviews }) => reviews)) ?? [], [data]);
  const selectedReview = useMemo(() => {
    return flatPages[selectedIndex];
  }, [selectedIndex, flatPages]);

  const modalTitle = currentContent === CONTENT_TYPE.DETAIL ? '사진 후기' : '사진 후기 전체보기';
  const firstPageReviews = head(data?.pages)?.reviews;

  const handleClickBackToList = useCallback(() => setCurrentContent(() => CONTENT_TYPE.LIST), []);

  const handleClickPrev = useCallback(() => {
    if (selectedIndex === 0) {
      Alert({ text: '첫번째 후기입니다.' });
      return;
    }
    setSelectedIndex(() => selectedIndex - 1);
  }, [selectedIndex]);

  const handleClickNext = useCallback(() => {
    const isLastItem = flatPages.length === selectedIndex + 1;
    const threshold = 20;

    if (isLastItem && !hasNextPage) {
      Alert({ text: '마지막 후기입니다.' });
      return;
    }

    if (hasNextPage && flatPages.length - selectedIndex < threshold) {
      fetchNextPage();
    }

    setSelectedIndex(() => selectedIndex + 1);
  }, [flatPages.length, hasNextPage, selectedIndex]);

  const handleClick = () => {
    try {
      amplitudeService.syncSetScreenName(ScreenName.PRODUCT_DETAIL_DESCRIPTION);
      amplitudeService.syncSetScreenName(ScreenName.PRODUCT_REVIEW_SUBTAB);
      amplitudeService.syncLogEvent(new SelectPhotoReviewList({ selectionType: 'more' }));
    } catch {}
    setIsOpen(true);
  };

  const handleClickImage = (index: number) => {
    setSelectedIndex(index);
    setCurrentContent(CONTENT_TYPE.DETAIL);
    setIsOpen(true);
  };

  const handleClickGalleryImage = (index: number, review: ProductReview) => {
    try {
      amplitudeService.syncSetScreenName(ScreenName.PRODUCT_DETAIL_DESCRIPTION);
      amplitudeService.syncSetScreenName(ScreenName.PRODUCT_REVIEW_SUBTAB);
      amplitudeService.syncLogEvent(
        new ViewReviewDetail({
          reviewData: review,
          sortType: 'RECENTLY',
          productData,
        }),
      );
    } catch {}
    handleClickImage(index);
  };

  const handleDismiss = () => {
    setCurrentContent(CONTENT_TYPE.LIST);
    setIsOpen(false);
  };

  return (
    <Wrapper>
      {isLoading && <Loading count={MAX_NUMBER_OF_REVIEW_GALLERY_IMAGE} />}
      {status === 'success' && !!firstPageReviews
        ? firstPageReviews.slice(0, MAX_NUMBER_OF_REVIEW_GALLERY_IMAGE).map((review, index) => (
            <Fragment key={review.id}>
              <ThumbnailImage
                url={head(review.images)?.url || NoMainImageLogo}
                onClick={() => handleClickGalleryImage(index, review)}
              />
              {index === 7 && firstPageReviews.length >= MAX_NUMBER_OF_REVIEW_GALLERY_IMAGE ? (
                <ShowMore onClick={handleClick}>
                  <span>+더보기</span>
                </ShowMore>
              ) : null}
            </Fragment>
          ))
        : null}
      <ReviewModal title={modalTitle} isOpen={isOpen} onDismiss={handleDismiss}>
        {currentContent === CONTENT_TYPE.LIST ? (
          <ReviewModalImageList onClickImage={handleClickImage} contentsProductNo={contentsProductNo} />
        ) : (
          <ReviewModalDetailContent
            review={selectedReview}
            onClickBackToList={handleClickBackToList}
            onClickPrev={handleClickPrev}
            onClickNext={handleClickNext}
          />
        )}
      </ReviewModal>
    </Wrapper>
  );
}
